/**
 * Vehicles List Page
 * 
 * Premium modern UI showing all vehicles in a grid/card layout
 * Allows adding new vehicles with VIN decoder
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import api from '../../../lib/api';
import { 
  Plus, 
  Search, 
  Truck, 
  Fuel, 
  MapPin, 
  X, 
  Activity, 
  Battery, 
  Gauge, 
  ChevronRight,
  ShieldCheck,
  Zap,
  Settings,
  Upload,
  Image as ImageIcon
} from 'lucide-react';

export default function Vehicles() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  console.log('🚗 [Vehicles Page] Component mounted');
  
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [prefilledIMEI, setPrefilledIMEI] = useState('');

  // Check for IMEI in URL params (from live tracking registration)
  useEffect(() => {
    const imei = searchParams.get('imei');
    if (imei) {
      setPrefilledIMEI(imei);
      setShowAddModal(true);
    }
  }, [searchParams]);

  // Load vehicles
  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/vehicles');
      setVehicles(data);
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter vehicles
  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      vehicle.vehicle_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.vin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.license_plate?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
      (filterStatus === 'active' && vehicle.status === 'Active') ||
      (filterStatus === 'inactive' && vehicle.status === 'Inactive') ||
      (filterStatus === 'in-repair' && vehicle.status === 'In Repair');
    
    return matchesSearch && matchesFilter;
  });

  // Calculate stats
  const activeCount = vehicles.filter(v => v.status === 'Active').length;
  const averageBattery = vehicles.length > 0
    ? Math.round(vehicles.reduce((sum, v) => sum + (v.battery_level || 0), 0) / vehicles.length)
    : 0;

  if (loading) {
    console.log('🚗 [Vehicles Page] Still loading...');
    return (
      <div className="h-screen w-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-slate-400 font-medium animate-pulse">Loading fleet...</p>
      </div>
    );
  }

  console.log('🚗 [Vehicles Page] ✅ Rendering content:', {
    vehiclesCount: vehicles.length,
    filteredCount: filteredVehicles.length
  });

  return (
    <div className="h-screen w-screen bg-slate-950 text-slate-100 flex flex-col overflow-hidden relative">
      {/* Background Ambience */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-600/10 blur-[120px] rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-600/5 blur-[150px] rounded-full"></div>
      </div>

      <header className="px-12 py-10 flex flex-col md:flex-row md:items-end justify-between gap-6 z-10">
        <div>
          <h1 className="text-5xl font-black tracking-tighter text-white">Vehicles</h1>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
            <input 
              type="text" 
              placeholder="Search assets..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-slate-900/50 backdrop-blur-md border border-slate-800 rounded-2xl pl-11 pr-6 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all w-64 md:w-80 text-white placeholder-slate-600"
            />
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3.5 rounded-2xl text-sm font-bold transition-all shadow-xl shadow-blue-600/20 active:scale-95"
          >
            <Plus className="w-4 h-4" />
            New Asset
          </button>
        </div>
      </header>

      {/* Quick Stats Overlay */}
      <div className="px-12 pb-8 flex gap-8 z-10 overflow-x-auto no-scrollbar">
        <QuickStat icon={<Activity className="w-4 h-4 text-emerald-400" />} label="Active" value={activeCount} />
        <QuickStat icon={<ShieldCheck className="w-4 h-4 text-blue-400" />} label="Total Fleet" value={vehicles.length} />
        <QuickStat icon={<Zap className="w-4 h-4 text-amber-400" />} label="Avg Battery" value={`${averageBattery}%`} />
      </div>

      <main className="flex-1 px-12 pb-12 overflow-y-auto no-scrollbar z-10">
        {filteredVehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Truck className="w-24 h-24 text-slate-700 mb-6" />
            <h3 className="text-2xl font-bold text-slate-300 mb-2">No vehicles found</h3>
            <p className="text-slate-500 mb-8">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first vehicle'
              }
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3.5 rounded-2xl text-sm font-bold transition-all shadow-xl shadow-blue-600/20"
              >
                <Plus className="w-4 h-4" />
                Add First Vehicle
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8">
            {filteredVehicles.map((vehicle) => {
              const displayName = vehicle.vehicle_name || `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
              const statusKey = vehicle.status?.toLowerCase().replace(' ', '-') || 'inactive';
              
              return (
                <div 
                  key={vehicle.id}
                  onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                  className="group relative bg-slate-900/40 backdrop-blur-xl border border-slate-800 p-6 rounded-[32px] cursor-pointer hover:border-blue-500/50 hover:bg-slate-900/60 transition-all duration-500 flex flex-col"
                >
                  {/* Status Badge */}
                  <div className="flex justify-between items-start mb-6">
                    <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                      statusKey === 'active' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' :
                      statusKey === 'in-repair' ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' :
                      'bg-slate-700/10 border-slate-700/20 text-slate-400'
                    }`}>
                      {vehicle.status || 'Unknown'}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingVehicle(vehicle);
                          setShowEditModal(true);
                        }}
                        className="p-2 rounded-full bg-slate-800/50 hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Settings className="w-4 h-4 text-slate-400" />
                      </button>
                      <div className="p-2 rounded-full bg-slate-800/50 opacity-0 group-hover:opacity-100 transition-opacity">
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Preview Image (Stylized) */}
                  <div className="relative h-32 mb-8 flex items-center justify-center">
                    <div className="absolute inset-0 bg-blue-600/5 blur-3xl rounded-full scale-50 group-hover:scale-100 transition-transform duration-700"></div>
                    <img 
                      src={vehicle.vehicle_image_url || "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&q=80&w=600"} 
                      alt={vehicle.model || 'Vehicle'} 
                      className="w-full h-full object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-110 -scale-x-100"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold tracking-tight mb-1 group-hover:text-blue-400 transition-colors">
                      {displayName}
                    </h3>
                    {vehicle.vehicle_name && (
                      <p className="text-slate-500 text-sm font-medium mb-6">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </p>
                    )}
                    {!vehicle.vehicle_name && vehicle.license_plate && (
                      <p className="text-slate-500 text-sm font-medium mb-6">
                        {vehicle.license_plate}
                      </p>
                    )}

                    <div className="grid grid-cols-2 gap-4 border-t border-slate-800 pt-6">
                      <div className="flex items-center gap-2">
                        <Battery className={`w-4 h-4 ${(vehicle.battery_level || 0) < 20 ? 'text-rose-500' : 'text-emerald-400'}`} />
                        <span className="text-sm font-bold text-slate-300">{vehicle.battery_level || 0}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Gauge className="w-4 h-4 text-slate-500" />
                        <span className="text-sm font-bold text-slate-300">
                          {vehicle.current_odometer ? `${(vehicle.current_odometer / 1000).toFixed(1)}k mi` : 'N/A'}
                        </span>
                      </div>
                      {vehicle.fuel_level !== undefined && (
                        <div className="flex items-center gap-2 col-span-2">
                          <Fuel className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-bold text-slate-300">{vehicle.fuel_level || 0}%</span>
                        </div>
                      )}
                      {vehicle.current_lat && vehicle.current_lng && (
                        <div className="flex items-center gap-2 col-span-2">
                          <MapPin className="w-4 h-4 text-emerald-400" />
                          <span className="text-sm font-bold text-slate-300">Located</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add New Placeholder Card */}
            <div 
              onClick={() => setShowAddModal(true)}
              className="border-2 border-dashed border-slate-800 rounded-[32px] p-8 flex flex-col items-center justify-center group hover:border-blue-500/50 hover:bg-blue-500/5 transition-all cursor-pointer"
            >
              <div className="p-4 rounded-full bg-slate-900 border border-slate-800 group-hover:bg-blue-600 group-hover:border-blue-500 transition-all mb-4">
                <Plus className="w-8 h-8 text-slate-500 group-hover:text-white" />
              </div>
              <p className="text-slate-500 group-hover:text-slate-300 font-bold transition-colors">Add New Asset</p>
            </div>
          </div>
        )}
      </main>

      {/* Add Vehicle Modal */}
      {showAddModal && (
        <AddVehicleModal 
          prefilledIMEI={prefilledIMEI}
          onClose={() => {
            setShowAddModal(false);
            setPrefilledIMEI('');
          }}
          onSuccess={() => {
            loadVehicles();
            setShowAddModal(false);
            setPrefilledIMEI('');
          }}
        />
      )}

      {/* Edit Vehicle Modal */}
      {showEditModal && editingVehicle && (
        <EditVehicleModal 
          vehicle={editingVehicle}
          onClose={() => {
            setShowEditModal(false);
            setEditingVehicle(null);
          }}
          onSuccess={() => {
            loadVehicles();
            setShowEditModal(false);
            setEditingVehicle(null);
          }}
        />
      )}
    </div>
  );
}

// Quick Stat Component
const QuickStat = ({ icon, label, value }) => (
  <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800 px-6 py-4 rounded-2xl flex items-center gap-4 min-w-[180px]">
    <div className="p-2.5 rounded-xl bg-slate-800 border border-slate-700">
      {icon}
    </div>
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
      <p className="text-xl font-black text-white">{value}</p>
    </div>
  </div>
);

// Add Vehicle Modal Component
function AddVehicleModal({ prefilledIMEI, onClose, onSuccess }) {
  const { isDark } = useTheme();
  const [step, setStep] = useState(1); // 1: Initial choice, 2: Details
  const [useVIN, setUseVIN] = useState(true); // Whether to decode VIN or skip
  const [vin, setVin] = useState('');
  const [vinLoading, setVinLoading] = useState(false);
  const [vinError, setVinError] = useState('');
  const [formData, setFormData] = useState({ imei: prefilledIMEI || '' });
  const [saving, setSaving] = useState(false);

  // Decode VIN
  const handleDecodeVIN = async () => {
    if (vin.length !== 17) {
      setVinError('VIN must be exactly 17 characters');
      return;
    }

    setVinLoading(true);
    setVinError('');

    try {
      const result = await api.post('/api/vehicles/decode-vin', { vin: vin.toUpperCase() });
      
      if (result.success && result.data) {
        setFormData({ ...formData, ...result.data });
        setStep(2);
      }
    } catch (err) {
      setVinError(err.message || 'Failed to decode VIN. You can still enter details manually.');
      // Allow manual entry
      setFormData({ ...formData, vin: vin.toUpperCase() });
      setStep(2);
    } finally {
      setVinLoading(false);
    }
  };
  
  // Skip VIN and go to manual entry
  const handleSkipVIN = () => {
    setUseVIN(false);
    setStep(2);
  };

  // Save vehicle
  const handleSave = async () => {
    // Validate required fields
    if (!formData.vehicle_name && !formData.make && !formData.model) {
      alert('Please provide either a Vehicle Name or Make/Model');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/api/vehicles', formData);
      onSuccess();
    } catch (err) {
      alert(err.message || 'Failed to create vehicle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-md">
      <div className="w-full max-w-xl bg-slate-900 border border-slate-800 rounded-[40px] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="px-8 py-8 flex justify-between items-start border-b border-slate-800">
          <div>
            <h2 className="text-2xl font-black tracking-tight text-white">
              {step === 1 ? 'Register New Asset' : 'Vehicle Details'}
            </h2>
            <p className="text-slate-500 text-sm font-medium mt-1">
              {step === 1 
                ? 'Add a vehicle to your fleet vision dashboard.'
                : 'Complete the vehicle information below.'
              }
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-slate-800 rounded-full text-slate-500 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 max-h-[70vh] overflow-y-auto">
          {step === 1 ? (
            <div className="space-y-6">
              <div 
                className={`p-6 bg-slate-950/50 border border-slate-800 rounded-3xl group hover:border-blue-500/50 transition-all cursor-pointer ${vinLoading ? 'pointer-events-none opacity-50' : ''}`}
                onClick={vin.length === 17 ? handleDecodeVIN : undefined}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="p-3 bg-blue-600/10 text-blue-400 rounded-2xl">
                    <Zap className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Automated VIN Decoder</h3>
                </div>
                <p className="text-slate-400 text-sm mb-6">
                  Input the 17-character VIN to automatically pull technical specs, battery health history, and performance benchmarks.
                </p>
                <div>
                  <input 
                    type="text" 
                    placeholder="Enter 17-character VIN..." 
                    value={vin}
                    onChange={(e) => {
                      setVin(e.target.value.toUpperCase());
                      setVinError('');
                    }}
                    maxLength={17}
                    className="w-full bg-slate-900 border border-slate-800 rounded-2xl px-6 py-4 text-white font-mono placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  />
                  {vinError && (
                    <p className="mt-2 text-sm text-amber-400">{vinError}</p>
                  )}
                  {vin.length === 17 && (
                    <button
                      onClick={handleDecodeVIN}
                      disabled={vinLoading}
                      className="mt-4 w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3.5 rounded-2xl text-sm font-bold transition-all shadow-xl shadow-blue-600/20"
                    >
                      {vinLoading ? 'Decoding VIN Metadata...' : 'Decode VIN & Continue'}
                    </button>
                  )}
                </div>
              </div>

              <div 
                className="p-6 bg-slate-950/50 border border-slate-800 rounded-3xl group hover:border-slate-600 transition-all cursor-pointer"
                onClick={handleSkipVIN}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="p-3 bg-slate-800 text-slate-400 rounded-2xl">
                    <Activity className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-300">Manual Entry</h3>
                </div>
                <p className="text-slate-500 text-sm">
                  Don't have a VIN handy? Add the basic details manually and update telematics later.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Vehicle Name - Optional, used for display */}
                <div className="col-span-2">
                  <label className="block text-sm font-semibold mb-2 text-slate-300">
                    Vehicle Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.vehicle_name || ''}
                    onChange={(e) => setFormData({...formData, vehicle_name: e.target.value})}
                    placeholder="e.g., Red Truck, Delivery Van #3"
                    className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  />
                  <p className="text-xs mt-2 text-slate-500">
                    This name will be displayed on the map view. If not provided, will use Make/Model.
                  </p>
                </div>
                
                {/* VIN - Optional if not decoded */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-300">
                    VIN {useVIN && '*'}
                  </label>
                  <input
                    type="text"
                    value={formData.vin || ''}
                    onChange={(e) => setFormData({...formData, vin: e.target.value.toUpperCase()})}
                    disabled={useVIN}
                    maxLength={17}
                    placeholder={!useVIN ? 'Enter VIN (optional)' : ''}
                    className={`w-full px-4 py-3 rounded-xl border font-mono ${
                      useVIN 
                        ? 'bg-slate-800 border-slate-700 text-slate-400'
                        : 'bg-slate-900 border-slate-700 text-white placeholder-slate-600'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50`}
                  />
                </div>
                
                {/* IMEI - Required for tracking */}
                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-300">
                    IMEI (GPS Tracker) *
                  </label>
                  <input
                    type="text"
                    value={formData.imei || ''}
                    onChange={(e) => setFormData({...formData, imei: e.target.value})}
                    placeholder="e.g. 359854085792618"
                    maxLength={20}
                    className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  />
                  <p className="text-xs mt-2 text-slate-500">
                    Required for live telemetry tracking
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-300">Year</label>
                  <input
                    type="number"
                    value={formData.year || ''}
                    onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-300">Make</label>
                  <input
                    type="text"
                    value={formData.make || ''}
                    onChange={(e) => setFormData({...formData, make: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-300">Model</label>
                  <input
                    type="text"
                    value={formData.model || ''}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-300">Color</label>
                  <input
                    type="text"
                    value={formData.color || ''}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    placeholder="White, Black, etc."
                    className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-300">License Plate</label>
                  <input
                    type="text"
                    value={formData.license_plate || ''}
                    onChange={(e) => setFormData({...formData, license_plate: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-300">Current Odometer (miles)</label>
                  <input
                    type="number"
                    value={formData.current_odometer || ''}
                    onChange={(e) => setFormData({...formData, current_odometer: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-slate-300">Status</label>
                  <select
                    value={formData.status || 'Active'}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="In Repair">In Repair</option>
                    <option value="Retired">Retired</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div className="px-8 py-6 bg-slate-950/50 border-t border-slate-800 flex justify-end gap-3">
            <button
              onClick={() => setStep(1)}
              className="px-8 py-3 rounded-2xl text-sm font-bold text-slate-400 hover:text-white transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-10 py-3 rounded-2xl text-sm font-bold transition-all shadow-xl shadow-blue-600/20"
            >
              {saving ? 'Saving...' : 'Save Vehicle'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Edit Vehicle Modal Component
function EditVehicleModal({ vehicle, onClose, onSuccess }) {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    vehicle_name: vehicle.vehicle_name || '',
    year: vehicle.year || '',
    make: vehicle.make || '',
    model: vehicle.model || '',
    trim: vehicle.trim || '',
    color: vehicle.color || '',
    license_plate: vehicle.license_plate || '',
    vin: vehicle.vin || '',
    status: vehicle.status || 'Active',
    notes: vehicle.notes || '',
    vehicle_image_url: vehicle.vehicle_image_url || ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(vehicle.vehicle_image_url || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert('Image must be less than 10MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      let imageUrl = formData.vehicle_image_url;

      // Upload image if a new one was selected
      if (imageFile) {
        setUploading(true);
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('files', imageFile);
          uploadFormData.append('category', 'vehicle-image');
          uploadFormData.append('isPublic', 'true');

          const uploadResult = await api.upload('/api/files/upload', uploadFormData);

          if (uploadResult.files && uploadResult.files.length > 0) {
            // Save the GCS path instead of signed URL (which expires)
            // The backend will generate fresh signed URLs when returning vehicles
            imageUrl = uploadResult.files[0].gcs_path || uploadResult.files[0].gcs_url || uploadResult.files[0].viewUrl;
          }
        } catch (uploadErr) {
          console.error('Image upload error:', uploadErr);
          alert('Failed to upload image. Saving vehicle without image update.');
        }
        setUploading(false);
      }

      // Update vehicle - only send non-empty values
      const updateData = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        // Only include non-empty values, or explicitly include empty strings for text fields
        if (value !== '' && value !== null && value !== undefined) {
          updateData[key] = value;
        } else if (key === 'notes' || key === 'vehicle_name') {
          // Allow empty strings for text fields
          updateData[key] = value || null;
        }
      });
      updateData.vehicle_image_url = imageUrl;

      await api.put(`/api/vehicles/${vehicle.id}`, updateData);

      onSuccess();
    } catch (err) {
      console.error('Failed to update vehicle:', err);
      alert('Failed to update vehicle. Please try again.');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border ${
        isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-6 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold text-white">Edit Vehicle</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Image Upload Section */}
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-slate-300">Vehicle Image</label>
            
            {/* Image Preview */}
            <div className="relative w-full h-48 bg-slate-800 rounded-xl border-2 border-dashed border-slate-700 overflow-hidden">
              {imagePreview ? (
                <img 
                  src={imagePreview} 
                  alt="Vehicle preview" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <ImageIcon className="w-12 h-12 mb-2" />
                  <p className="text-sm">No image uploaded</p>
                </div>
              )}
            </div>

            {/* Upload Button */}
            <div className="flex gap-3">
              <label className="flex-1 cursor-pointer">
                <div className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors">
                  <Upload className="w-4 h-4" />
                  {imageFile ? 'Change Image' : 'Upload Image'}
                </div>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </label>
              {imagePreview && (
                <button
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                    setFormData({...formData, vehicle_image_url: ''});
                  }}
                  className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-semibold transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
            {imageFile && (
              <p className="text-sm text-slate-400">
                Selected: {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-semibold mb-2 text-slate-300">Vehicle Name</label>
              <input
                type="text"
                value={formData.vehicle_name}
                onChange={(e) => setFormData({...formData, vehicle_name: e.target.value})}
                placeholder="e.g., Fleet Truck #1"
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Year</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Make</label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => setFormData({...formData, make: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Model</label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({...formData, model: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Trim</label>
              <input
                type="text"
                value={formData.trim}
                onChange={(e) => setFormData({...formData, trim: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Color</label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">License Plate</label>
              <input
                type="text"
                value={formData.license_plate}
                onChange={(e) => setFormData({...formData, license_plate: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">VIN</label>
              <input
                type="text"
                value={formData.vin}
                onChange={(e) => setFormData({...formData, vin: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="In Repair">In Repair</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-semibold mb-2 text-slate-300">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 p-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving || uploading}
            className="px-6 py-3 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 font-semibold transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {uploading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
            {uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
